import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { FaPaperPlane, FaTrash, FaUserTag, FaArrowLeft, FaExclamationTriangle } from "react-icons/fa";
import config from "../config";
import AdminSidebarShell from "./AdminSidebarShell";

const GroupDetails = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const groupsBaseUrl = `${config.API_BASE_URL}/admin/groups`;

  const [groupName, setGroupName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("Remote Worker");
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [inviteToDelete, setInviteToDelete] = useState(null);

  /* ======================
     FETCH GROUP DETAILS
  ====================== */
  const fetchGroupDetails = async () => {
    try {
      const res = await axios.get(
        `${groupsBaseUrl}/${groupId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGroupName(res.data.name);
    } catch {
      setGroupName("Unknown Group");
    }
  };

  /* ======================
     FETCH INVITES
  ====================== */
  const fetchInvites = async () => {
    try {
      const res = await axios.get(
        `${groupsBaseUrl}/${groupId}/invites`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInvites(res.data);
    } catch {
      setError("Failed to load invites");
    }
  };

  useEffect(() => {
    fetchGroupDetails();
    fetchInvites();
  }, [groupId]);

  /* ======================
     SEND INVITE
  ====================== */
  const sendInvite = async () => {
    if (!email) {
      setError("Email is required");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      await axios.post(
        `${groupsBaseUrl}/${groupId}/invite`,
        { email, role },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage("Invite sent successfully");
      setEmail("");
      setRole("Remote Worker");
      fetchInvites();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send invite");
    } finally {
      setLoading(false);
    }
  };

  /* ======================
     RESEND INVITE
  ====================== */
  const resendInvite = async (inviteToResend) => {
    setLoading(true);
    try {
      await axios.post(
        `${groupsBaseUrl}/${groupId}/invite`,
        {
          email: inviteToResend.email,
          role: inviteToResend.role || "Remote Worker"
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage(`Invite resent to ${inviteToResend.email}`);
    } catch {
      setError("Failed to resend invite");
    } finally {
      setLoading(false);
    }
  };

  /* ======================
     DELETE INVITE (MODAL)
  ====================== */
  const confirmDeleteInvite = (emailToDelete) => {
    setInviteToDelete(emailToDelete);
    setShowDeleteModal(true);
  };

  const executeDeleteInvite = async () => {
    if (!inviteToDelete) return;
    setLoading(true);
    try {
      await axios.delete(
        `${groupsBaseUrl}/${groupId}/invites`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: { email: inviteToDelete }
        }
      );
      setShowDeleteModal(false);
      setInviteToDelete(null);
      fetchInvites();
    } catch {
      setError("Failed to delete invite");
      setShowDeleteModal(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminSidebarShell activeItem="groupadmin">
      <div style={contentWrapper}>
        {/* 🔙 BACK BUTTON */}
        <button style={backBtn} onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>

        {/* ======================
            GROUP INFO
        ====================== */}
        <div style={groupInfo}>
          <h2 style={title}>{groupName}</h2>
          <p style={subtitle}>
            Invite members to this group and manage their roles
          </p>
        </div>

        <div style={mainGrid}>
          {/* ======================
              INVITE FORM
          ====================== */}
          <div style={card}>
            <h3 style={cardTitle}>Invite Member</h3>

            {message && <p style={success}>{message}</p>}
            {error && <p style={errorStyle}>{error}</p>}

            <div style={inputGroup}>
              <label style={label}>Email Address</label>
              <input
                style={input}
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div style={inputGroup}>
              <label style={label}>Role</label>
              <div style={selectWrapper}>
                <FaUserTag style={selectIcon} />
                <select
                  style={selectInputClean}
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                >
                  <option value="Resident User">Resident User</option>
                  <option value="Remote Worker">Remote Worker</option>
                </select>
              </div>
            </div>

            <button style={primaryBtn} onClick={sendInvite} disabled={loading}>
              <FaPaperPlane /> {loading ? "Sending..." : "Send Invite"}
            </button>
          </div>

          {/* ======================
              INVITE LIST
          ====================== */}
          <div style={listCard}>
            <h3 style={cardTitle}>Sent Invitations</h3>

            {invites.length === 0 && (
              <p style={emptyStateText}>No invitations yet. Send one to get started.</p>
            )}

            <div style={inviteListContainer}>
              {invites.map((invite, index) => (
                <div key={`${invite.email}-${index}`} style={inviteCard}>
                  <div style={inviteContent}>
                    <div style={inviteHeader}>
                      <strong style={emailStyle}>{invite.email}</strong>
                      <span style={status(invite.status)}>
                        {invite.status}
                      </span>
                    </div>

                    <div style={detailsRow}>
                      <span style={roleBadge}>{invite.role || "Remote Worker"}</span>
                      <span style={groupBadge}>
                        Group: {invite.groupName || groupName}
                      </span>
                    </div>
                  </div>

                  {invite.status === "pending" && (
                    <div style={actions}>
                      <button
                        style={iconBtn}
                        onClick={() => resendInvite(invite)}
                        title="Resend Invite"
                      >
                        <FaPaperPlane />
                      </button>

                      <button
                        style={dangerBtn}
                        onClick={() => confirmDeleteInvite(invite.email)}
                        title="Delete Invite"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ======================
            DELETE MODAL
        ====================== */}
        {showDeleteModal && (
          <div style={modalOverlay}>
            <div style={modalContent}>
              <div style={modalIconWrapper}>
                <FaExclamationTriangle style={modalIcon} />
              </div>
              <h3 style={modalTitle}>Delete Invitation?</h3>
              <p style={modalText}>
                Are you sure you want to delete the invitation for <strong>{inviteToDelete}</strong>? This action cannot be undone.
              </p>
              <div style={modalActions}>
                <button
                  style={cancelModalBtn}
                  onClick={() => setShowDeleteModal(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  style={confirmModalBtn}
                  onClick={executeDeleteInvite}
                  disabled={loading}
                >
                  {loading ? "Deleting..." : "Delete Invite"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminSidebarShell>
  );
};

export default GroupDetails;

/* ======================
   STYLES
===================== */

const contentWrapper = {
  maxWidth: "1000px",
  margin: "0 auto",
  fontFamily: "system-ui, -apple-system, sans-serif"
};

const backBtn = {
  padding: "8px 16px",
  background: "rgba(14, 85, 128, 0.05)",
  border: "none",
  color: "#0E5580",
  borderRadius: "8px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "600",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "32px",
  transition: "background 0.2s"
};

const groupInfo = {
  marginBottom: "32px",
  animation: "fadeUp 0.3s ease-out"
};

const title = {
  fontSize: "28px",
  fontWeight: "700",
  color: "#1e293b",
  margin: "0 0 8px 0"
};

const subtitle = {
  fontSize: "16px",
  color: "#64748b",
  margin: 0
};

const mainGrid = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "24px",
  animation: "fadeUp 0.4s ease-out",
  '@media (minWidth: 768px)': {
    gridTemplateColumns: "1fr 1fr"
  }
};

const card = {
  background: "#ffffff",
  padding: "32px",
  borderRadius: "16px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
  border: "1px solid #e2e8f0",
  alignSelf: "start"
};

const listCard = {
  background: "#ffffff",
  padding: "32px",
  borderRadius: "16px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
  border: "1px solid #e2e8f0"
};

const cardTitle = {
  fontSize: "18px",
  fontWeight: "600",
  color: "#1e293b",
  margin: "0 0 24px 0"
};

const inputGroup = {
  marginBottom: "20px"
};

const label = {
  display: "block",
  fontSize: "14px",
  fontWeight: "500",
  color: "#475569",
  marginBottom: "8px"
};

const input = {
  width: "100%",
  padding: "12px 16px",
  borderRadius: "10px",
  border: "1px solid #cbd5e1",
  background: "#f8fafc",
  fontSize: "15px",
  color: "#1e293b",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.2s, background 0.2s"
};

const selectWrapper = {
  position: "relative"
};

const selectIcon = {
  position: "absolute",
  top: "50%",
  left: "16px",
  transform: "translateY(-50%)",
  color: "#64748b",
  fontSize: "14px",
  pointerEvents: "none"
};

const selectInputClean = {
  width: "100%",
  padding: "12px 16px 12px 40px",
  borderRadius: "10px",
  border: "1px solid #cbd5e1",
  background: "#f8fafc",
  fontSize: "15px",
  color: "#1e293b",
  appearance: "none",
  cursor: "pointer",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.2s, background 0.2s"
};

const primaryBtn = {
  width: "100%",
  padding: "14px",
  background: "#0E5580",
  color: "#fff",
  border: "none",
  borderRadius: "10px",
  cursor: "pointer",
  fontSize: "16px",
  fontWeight: "600",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  marginTop: "12px",
  transition: "background 0.2s, opacity 0.2s"
};

const emptyStateText = {
  color: "#64748b",
  fontSize: "15px",
  textAlign: "center",
  padding: "24px 0"
};

const inviteListContainer = {
  display: "flex",
  flexDirection: "column",
  gap: "16px"
};

const inviteCard = {
  padding: "20px",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  background: "#f8fafc",
  gap: "16px"
};

const inviteContent = {
  flex: 1,
  minWidth: 0
};

const inviteHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "12px"
};

const emailStyle = {
  fontSize: "15px",
  fontWeight: "600",
  color: "#1e293b",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  marginRight: "12px"
};

const detailsRow = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap"
};

const roleBadge = {
  background: "rgba(14, 85, 128, 0.1)",
  color: "#0E5580",
  padding: "4px 12px",
  borderRadius: "20px",
  fontSize: "12px",
  fontWeight: "600"
};

const groupBadge = {
  background: "#f1f5f9",
  color: "#475569",
  padding: "4px 12px",
  borderRadius: "20px",
  fontSize: "12px",
  fontWeight: "600"
};

const actions = {
  display: "flex",
  gap: "8px",
  flexShrink: 0
};

const iconBtn = {
  background: "#f0fdf4",
  color: "#16a34a",
  border: "1px solid #bbf7d0",
  padding: "10px",
  borderRadius: "8px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.2s"
};

const dangerBtn = {
  background: "#fef2f2",
  color: "#dc2626",
  border: "1px solid #fecaca",
  padding: "10px",
  borderRadius: "8px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.2s"
};

const status = (value) => ({
  padding: "4px 12px",
  borderRadius: "20px",
  fontSize: "12px",
  fontWeight: "600",
  background: value === "accepted" ? "#dcfce7" : "#fef3c7",
  color: value === "accepted" ? "#166534" : "#92400e"
});

const success = {
  color: "#059669",
  fontSize: "14px",
  marginBottom: "16px",
  padding: "12px",
  background: "#ecfdf5",
  borderRadius: "8px",
  border: "1px solid #d1fae5"
};

const errorStyle = {
  color: "#dc2626",
  fontSize: "14px",
  marginBottom: "16px",
  padding: "12px",
  background: "#fef2f2",
  borderRadius: "8px",
  border: "1px solid #fee2e2"
};

// Modal Styles
const modalOverlay = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(15, 23, 42, 0.6)",
  backdropFilter: "blur(4px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  animation: "fadeIn 0.2s ease-out"
};

const modalContent = {
  background: "#fff",
  borderRadius: "20px",
  padding: "32px",
  width: "90%",
  maxWidth: "400px",
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  animation: "slideUp 0.3s ease-out",
  textAlign: "center"
};

const modalIconWrapper = {
  width: "48px",
  height: "48px",
  borderRadius: "50%",
  background: "#fef2f2",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 16px auto"
};

const modalIcon = {
  fontSize: "24px",
  color: "#ef4444"
};

const modalTitle = {
  fontSize: "20px",
  fontWeight: "700",
  color: "#0f172a",
  margin: "0 0 12px 0"
};

const modalText = {
  fontSize: "15px",
  color: "#64748b",
  lineHeight: "1.5",
  margin: "0 0 24px 0"
};

const modalActions = {
  display: "flex",
  gap: "12px"
};

const cancelModalBtn = {
  flex: 1,
  padding: "12px",
  background: "#f8fafc",
  color: "#475569",
  border: "1px solid #cbd5e1",
  borderRadius: "10px",
  fontSize: "15px",
  fontWeight: "600",
  cursor: "pointer",
  transition: "all 0.2s"
};

const confirmModalBtn = {
  flex: 1,
  padding: "12px",
  background: "#ef4444",
  color: "#fff",
  border: "none",
  borderRadius: "10px",
  fontSize: "15px",
  fontWeight: "600",
  cursor: "pointer",
  transition: "background 0.2s"
};
